"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { AuthLoginData } from '@/lib/api/types/auth';
import { getAuthUser, hasAuthToken, clearAuthCookies } from '@/lib/cookie-utils';
import { apiClient } from '@/lib/api';

/**
 * 인증 컨텍스트
 *
 * JWT는 httpOnly 쿠키에 저장되어 JS에서 접근 불가합니다.
 * user 상태는 토큰 제외 메타데이터만 포함하며, auth_user 쿠키에서 복원됩니다.
 * apiClient의 token 멤버는 더 이상 사용하지 않습니다
 * (fetch 요청 시 쿠키가 자동 전송되고, 프록시가 Authorization 헤더를 주입합니다).
 */

// auth-context에서 사용하는 사용자 타입 (token 필드 제외)
type UserMeta = Omit<AuthLoginData, 'token'>;

interface AuthContextType {
  user: UserMeta | null;
  setUser: (user: UserMeta | null) => void;
  isAuthenticated: boolean;
  login: (userData: UserMeta) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<UserMeta | null>(null);

  // 앱 초기화: auth_user 쿠키에서 메타데이터 복원
  useEffect(() => {
    if (typeof window !== 'undefined' && hasAuthToken()) {
      const saved = getAuthUser();
      if (saved) {
        setUserState(saved);
      }
    }
  }, []);

  const setUser = (userData: UserMeta | null) => {
    setUserState(userData);
  };

  /**
   * 로그인 성공 후 클라이언트 상태만 업데이트합니다.
   * httpOnly 쿠키 설정은 /api/auth/login route가 이미 완료했습니다.
   */
  const login = (userData: UserMeta) => {
    setUserState(userData);
    // apiClient의 메모리 토큰은 더 이상 사용하지 않음
    apiClient.clearToken();
  };

  /**
   * 로그아웃: 서버사이드 route를 통해 httpOnly 쿠키 삭제
   */
  const logout = async () => {
    setUserState(null);
    await clearAuthCookies();
    apiClient.clearToken();
  };

  // auth_user 쿠키가 있고 user 상태가 채워진 경우 인증된 것으로 간주
  const isAuthenticated = !!user && hasAuthToken();

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
